import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormControl,
  Validators
} from "@angular/forms";
import { Observable, of } from "rxjs";
import { debounceTime, map, switchMap, tap } from "rxjs/operators";
import { MatOptionSelectionChange } from "@angular/material/core";
import { LoadingDrivebService } from "@shared/components/loading-driveb/loading-driveb.service";
import {
  PartManufactirerFindByFiltersDTO,
  PartManufacturerDTO
} from "@shared/models/part-manufacturer-dto";
import { PartManufacturerService } from "@core/services/part-manufacturer.service";
import { MessageBeforeManuallyAddPartActionsEnum } from "@shared/enums/message-before-manually-add-part-actions.enum";

@Component({
    selector: "app-smartfleet-part-manufacturer-auto-complete",
    templateUrl: "./part-manufacturer-auto-complete.component.html",
    styleUrls: ["./part-manufacturer-auto-complete.component.scss"],
    standalone: false
})
export class PartManufacturerAutoCompleteComponent implements OnInit {
  loading = false;
  partManufacturer = new UntypedFormControl();
  searchForm = this.fb.group({
    name: this.partManufacturer
  });
  manuallyAddPartHintAction: MessageBeforeManuallyAddPartActionsEnum;
  lastDateUpdateManuallyAddPartHintAction: number;
  showManuallyAddPartHint = false;

  partManufacturers$: Observable<PartManufacturerDTO[]>;

  @ViewChild("partManufacturerInput")
  partManufacturerInput: ElementRef<HTMLInputElement>;

  @Input() set setManuallyAddPartHintAction(
    value: MessageBeforeManuallyAddPartActionsEnum
  ) {
    this.manuallyAddPartHintAction = value;
  }

  @Input() set setLastDateUpdateManuallyAddPartHintAction(value: number) {
    if (
      this.manuallyAddPartHintAction ==
      MessageBeforeManuallyAddPartActionsEnum.USE_EXISTING_PART
    ) {
      this.partManufacturerInput.nativeElement.focus();
      this.showManuallyAddPartHint = true;
    } else {
      this.showManuallyAddPartHint = false;
      this.clean();
    }
  }

  @Input() set required(value: boolean) {
    if (value && !this.disabled) {
      this.searchForm.controls.name.setValidators([Validators.required]);
    } else {
      this.searchForm.controls.name.clearValidators();
    }
    this.searchForm.updateValueAndValidity();
  }
  @Input() set disabled(value: boolean) {
    if (value) {
      this.searchForm.disable();
    } else {
      this.searchForm.enable();
    }
  }

  @Input() set value(value: string) {
    if (value) {
      this.searchForm.get("name").setValue(value);
    }
  }

  @Output() onChange: any = new EventEmitter<PartManufacturerDTO>();

  constructor(
    private fb: UntypedFormBuilder,
    private partManufacturerService: PartManufacturerService,
    private loadingDrivebService: LoadingDrivebService
  ) {}

  ngOnInit(): void {
    this.partManufacturers$ = this.partManufacturer.valueChanges.pipe(
      debounceTime(200),
      tap(
        () => (
          (this.loading = true), this.loadingDrivebService.setAutoMode(false)
        )
      ),
      switchMap((value: string) => {
        if (value) {
          const searchForm = {} as PartManufactirerFindByFiltersDTO;
          searchForm.name = value;
          return this.partManufacturerService.findByFilters(searchForm);
        } else {
          this.onChange.emit({} as PartManufacturerDTO);
          return of([]) as Observable<PartManufacturerDTO[]>;
        }
      }),
      map((result) => {
        this.loadingDrivebService.setAutoMode(true);
        this.loading = false;
        return result;
      })
    );
  }

  handlePartManufacturerChange(
    event: MatOptionSelectionChange,
    partManufacturer: PartManufacturerDTO
  ) {
    if (event.isUserInput) this.onChange.emit(partManufacturer);
  }

  clean() {
    this.searchForm.reset();
  }

  markAsTouched() {
    this.searchForm.get("name").markAsTouched();
  }
}
